import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowsClockwise, CheckCircle, Compass, Spinner } from "@phosphor-icons/react";
import { api, rememberNeedsSecret } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Disclaimer from "../components/Disclaimer";
import { apiError } from "../lib/errors";

const STEP_LABELS = {
  my_position: "Your position",
  my_needs: "Your needs",
  their_action: "The other person",
  their_needs: "Their imagined needs",
  synthesis: "Done — synthesis ready",
};

export default function Needs() {
  const nav = useNavigate();
  const { leader } = useAuth();
  const [situation, setSituation] = useState("");
  const [busy, setBusy] = useState(false);
  const [priorSessions, setPriorSessions] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    if (!leader) return;
    let alive = true;
    api.get("/needs")
      .then(({ data }) => { if (alive) setPriorSessions(data?.sessions || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoadingList(false); });
    return () => { alive = false; };
  }, [leader]);

  if (!leader) {
    return <Navigate to="/auth/login?next=/needs" replace state={{ from: "/needs" }} />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (situation.trim().length < 10) {
      toast.error("Please describe what happened (at least a couple of sentences).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/needs/start", { situation: situation.trim() });
      if (data?.access_secret) rememberNeedsSecret(data.id, data.access_secret);
      nav(`/needs/${data.id}`);
    } catch (err) {
      toast.error(apiError(err, "Could not start the exercise"));
    } finally {
      setBusy(false);
    }
  };

  const inProgress = priorSessions.filter((s) => !s.is_complete);
  const completed = priorSessions.filter((s) => s.is_complete);

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 lg:py-24">
      <div className="anim-in delay-1">
        <div className="label-kicker" style={{ color: "#1E3A2B" }}>Find My Real Needs</div>
        <h1 className="font-serif text-4xl lg:text-5xl text-[#1E3A2B] mt-3 tracking-tight leading-tight">
          From position to need.
        </h1>
        <p className="text-[#4A524C] mt-5 leading-relaxed">
          You have a conflict with someone. Before you respond, stop for a few
          minutes and identify what you both actually need underneath the
          positions you're each defending.
        </p>
        <p className="text-[#4A524C] mt-3 leading-relaxed">
          Your <em>position</em> is what you're demanding, defending, or
          accusing. Your <em>need</em> is what you really care about — the
          problem you're trying to solve deep down. This exercise helps you
          find it — for yourself first, then for the other person.
        </p>
      </div>

      {/* Resume in-progress sessions */}
      {!loadingList && inProgress.length > 0 && (
        <div className="mt-10 anim-in delay-2" data-testid="needs-inprogress-list">
          <div className="label-kicker mb-3" style={{ color: "#C77D5E" }}>Pick up where you left off</div>
          <div className="space-y-2">
            {inProgress.map((s) => (
              <Link
                key={s.id}
                to={`/needs/${s.id}`}
                data-testid={`needs-resume-${s.id}`}
                className="block bg-[#FAEFE8] border border-[#C77D5E]/30 rounded-2xl px-5 py-4 hover:border-[#C77D5E] transition"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[#1E3A2B] leading-relaxed line-clamp-2">{s.preview || "(no summary yet)"}</p>
                    <div className="text-xs text-[#8A948E] mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>Next: <strong className="text-[#C77D5E]">{STEP_LABELS[s.step] || s.step}</strong></span>
                      {s.created_at && (
                        <span>Started {new Date(s.created_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  <ArrowsClockwise size={16} weight="duotone" className="text-[#C77D5E] flex-shrink-0 mt-0.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-10 space-y-5 anim-in delay-2" data-testid="needs-intake-form">
        <label className="block">
          <span className="label-kicker block mb-2">
            {inProgress.length > 0 ? "Or start a new one" : "What happened?"}
          </span>
          <textarea
            data-testid="needs-situation-input"
            value={situation}
            onChange={(e) => setSituation(e.target.value)}
            rows={6}
            placeholder="Describe the incident or conflict as plainly as you can. Who did what. Skip the interpretation for now — just the facts."
            className="w-full bg-white border border-[#E8E4DB] rounded-2xl p-4 text-base text-[#121815] focus:outline-none focus:border-[#1E3A2B]"
          />
        </label>
        <div className="flex items-center justify-between gap-4 pt-2">
          <button
            type="submit"
            disabled={busy || !situation.trim()}
            data-testid="needs-begin-button"
            className="inline-flex items-center gap-2 bg-[#1E3A2B] text-white px-6 py-3 rounded-2xl hover:bg-[#2C4C3B] disabled:opacity-60 transition"
          >
            {busy ? <Spinner size={16} className="animate-spin" /> : <Compass size={16} weight="duotone" />}
            Begin the exercise <ArrowRight size={16} weight="bold" />
          </button>
          <span className="text-xs text-[#8A948E]">Takes ~10 minutes. Confidential.</span>
        </div>
        <Disclaimer variant="compact" className="mt-6 max-w-2xl" />
      </form>

      {/* Completed sessions */}
      {!loadingList && completed.length > 0 && (
        <div className="mt-12 anim-in delay-3" data-testid="needs-completed-list">
          <div className="label-kicker mb-3" style={{ color: "#1E3A2B" }}>Completed exercises</div>
          <div className="space-y-2">
            {completed.map((s) => (
              <Link
                key={s.id}
                to={`/needs/${s.id}`}
                data-testid={`needs-completed-${s.id}`}
                className="block bg-white border border-[#E8E4DB] rounded-2xl px-5 py-3 hover:border-[#1E3A2B] transition"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[#121815] leading-relaxed line-clamp-1 text-sm">{s.preview}</p>
                    <div className="text-xs text-[#8A948E] mt-1 flex flex-wrap gap-x-3">
                      <span>{s.my_confirmed_count} of your needs · {s.their_confirmed_count} of theirs</span>
                      {s.created_at && (
                        <span>{new Date(s.created_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  <CheckCircle size={16} weight="fill" className="text-[#1E3A2B] flex-shrink-0 mt-0.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

